import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, UploadCloud, CheckCircle2 } from "lucide-react";

export default function ApplyForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [answers, setAnswers] = useState({});
  const [resumeMode, setResumeMode] = useState("upload"); // upload | link
  const [resumeUrl, setResumeUrl] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [uploadedPath, setUploadedPath] = useState("");
  const [uploadName, setUploadName] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setError("Job not found"));
  }, [id]);

  const setAnswer = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));

  const uploadResume = async () => {
    if (!resumeFile) return;
    setError("");
    const fd = new FormData();
    fd.append("file", resumeFile);
    try {
      const { data } = await api.post("/upload/resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadedPath(data.storage_path);
      setUploadName(data.filename);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Upload failed. Try the link option instead.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        answers,
        resume_url: resumeMode === "link" ? resumeUrl : "",
        resume_storage_path: resumeMode === "upload" ? uploadedPath : "",
      };
      const { data } = await api.post(`/jobs/${id}/apply`, payload);
      setDone(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  if (!job) return <div className="p-16">{error || "Loading…"}</div>;

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <CheckCircle2 className="w-12 h-12 text-[var(--brand-primary)] mx-auto" />
        <h1 className="font-display font-black text-5xl mt-6 tracking-tight">Application sent.</h1>
        <p className="text-[#5C5C5C] mt-3">We notified <strong>{job.company}</strong>. Track its status anytime.</p>
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/candidate" data-testid="apply-done-track" className="btn-ink">My applications</Link>
          <Link to="/jobs" className="btn-ghost">Browse more roles</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
      <Link to={`/jobs/${id}`} className="inline-flex items-center gap-1 text-sm font-semibold hover:text-[var(--brand-primary)]">
        <ArrowLeft className="w-4 h-4" /> Back to role
      </Link>

      <div className="mt-6 border-b border-[#E2E0D8] pb-6">
        <div className="label-eyebrow">{job.company}</div>
        <h1 className="font-display font-black text-4xl mt-2 tracking-tight">Apply: {job.title}</h1>
        <p className="text-[#5C5C5C] mt-2 text-sm">Signed in as <strong>{user?.name || user?.email}</strong></p>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-8">
        {job.require_resume === false && (job.questions || []).length === 0 && (
          <div data-testid="q-fallback_experience">
            <label className="label-eyebrow block mb-2">
              1. {job.fallback_question || "Please describe your relevant skills and experience for this role."} *
            </label>
            <textarea
              required
              value={answers["fallback_experience"] || ""}
              onChange={(e) => setAnswer("fallback_experience", e.target.value)}
              rows={5}
              className="w-full border border-[#0F0F0F] px-4 py-3 rounded-sm bg-transparent text-[#0F0F0F]"
              data-testid="answer-fallback_experience"
              placeholder="Your answer..."
            />
          </div>
        )}

        {(job.questions || []).filter((q) => {
          if (!q.show_if_question_id) return true;
          const dep = (job.questions || []).find((p) => (p.id || "") === q.show_if_question_id);
          if (!dep) return true;
          const answer = (answers[dep.id] ?? "").toString().toLowerCase().trim();
          if (!answer) return false;
          const expected = (q.show_if_value || "").split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
          return expected.length === 0 || expected.includes(answer);
        }).map((q, idx) => (
          <div key={q.id} data-testid={`q-${q.id}`}>
            <label className="label-eyebrow block mb-2">{idx + 1}. {q.label}{q.required && " *"}</label>
            {q.type === "text" && (
              <input required={q.required} value={answers[q.id] || ""} onChange={(e) => setAnswer(q.id, e.target.value)}
                className="w-full border border-[#0F0F0F] px-4 py-3 rounded-sm bg-transparent"
                data-testid={`answer-${q.id}`} />
            )}
            {q.type === "textarea" && (
              <textarea required={q.required} value={answers[q.id] || ""} onChange={(e) => setAnswer(q.id, e.target.value)} rows={5}
                className="w-full border border-[#0F0F0F] px-4 py-3 rounded-sm bg-transparent"
                data-testid={`answer-${q.id}`} />
            )}
            {q.type === "url" && (
              <input type="url" required={q.required} value={answers[q.id] || ""} onChange={(e) => setAnswer(q.id, e.target.value)}
                placeholder="https://…"
                className="w-full border border-[#0F0F0F] px-4 py-3 rounded-sm bg-transparent"
                data-testid={`answer-${q.id}`} />
            )}
            {q.type === "select" && (
              <select required={q.required} value={answers[q.id] || ""} onChange={(e) => setAnswer(q.id, e.target.value)}
                className="w-full border border-[#0F0F0F] px-4 py-3 rounded-sm bg-transparent"
                data-testid={`answer-${q.id}`}>
                <option value="">Select…</option>
                {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            {q.type === "file" && (
              <input type="file" required={q.required} onChange={(e) => setAnswer(q.id, e.target.files?.[0]?.name || "")}
                className="block w-full text-sm"
                data-testid={`answer-${q.id}`} />
            )}
          </div>
        ))}

        {job.require_resume !== false && (
          <div>
            <label className="label-eyebrow block mb-3">Resume *</label>
            <div className="inline-flex border border-[#0F0F0F] rounded-sm overflow-hidden mb-3">
              {["upload", "link"].map((m) => (
                <button key={m} type="button" data-testid={`resume-mode-${m}`}
                  onClick={() => setResumeMode(m)}
                  className={`px-4 py-2 text-sm font-semibold ${resumeMode === m ? "bg-[#0F0F0F] text-[#FDFCF8]" : "bg-transparent"}`}>
                  {m === "upload" ? "Upload file" : "Paste link"}
                </button>
              ))}
            </div>

            {resumeMode === "upload" ? (
              <div className="border border-dashed border-[#0F0F0F] p-6 rounded-sm">
                <div className="flex items-center gap-4">
                  <UploadCloud className="w-6 h-6 text-[var(--brand-primary)]" />
                  <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setResumeFile(e.target.files?.[0])}
                    data-testid="resume-file" className="text-sm" />
                  <button type="button" onClick={uploadResume} disabled={!resumeFile}
                    data-testid="resume-upload-btn" className="btn-ink text-sm disabled:opacity-50">Upload</button>
                </div>
                {uploadedPath && (
                  <div className="mt-3 text-sm text-green-700 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Uploaded: {uploadName}
                  </div>
                )}
                <p className="text-xs text-[#5C5C5C] mt-3">PDF or DOC/DOCX. Max 10MB. Object storage powered.</p>
              </div>
            ) : (
              <input type="url" required value={resumeUrl} onChange={(e) => setResumeUrl(e.target.value)}
                placeholder="https://your-portfolio.com/resume.pdf"
                data-testid="resume-link"
                className="w-full border border-[#0F0F0F] px-4 py-3 rounded-sm bg-transparent" />
            )}
          </div>
        )}

        {error && <div data-testid="apply-error" className="text-sm text-[var(--brand-primary)] font-medium">{error}</div>}

        <button data-testid="apply-submit" type="submit" disabled={submitting}
          className="btn-terra text-base w-full sm:w-auto">
          {submitting ? "Sending…" : "Submit application"}
        </button>
      </form>
    </div>
  );
}
